// A room's type is the first segment of its URL: /deal-room/<slug>,
// /content-optimization/<slug>, /pitch/<slug>. Every room link, client and
// API, is built here so a new link inherits the scheme, and every incoming
// path is parsed here so a new type is one entry in ROOM_TYPE_PREFIX.
// /rooms/<slug>, the path until 2026-09-18, redirects to /deal-room/<slug>
// (routes/LegacyRoomRedirect.tsx) and must keep working: it is in sent
// emails, Pipedrive and unexpired magic links.
//
// Access, invites and recipients are the same for every type; the type only
// picks the URL segment, the label a reader sees ("pitch room") and, on the
// hub side, which skill shapes the room and how invitees are chosen.
export const ROOM_TYPES = ["deal_room", "content_optimization", "pitch"] as const;
export type RoomType = (typeof ROOM_TYPES)[number];

export const DEFAULT_ROOM_TYPE: RoomType = "deal_room";

export const ROOM_TYPE_PREFIX: Record<RoomType, string> = {
    deal_room: "/deal-room",
    content_optimization: "/content-optimization",
    pitch: "/pitch",
};

// What a reader is told they are signing in to ("Sign in to Acme's pitch
// room", "Your invitation to the Acme deal room").
export const ROOM_TYPE_LABEL: Record<RoomType, string> = {
    deal_room: "deal room",
    content_optimization: "content optimization room",
    pitch: "pitch room",
};

export const DEAL_ROOM_PREFIX = ROOM_TYPE_PREFIX.deal_room;
export const LEGACY_ROOM_PREFIX = "/rooms";

export function roomPath(slug: string, preview = false, type: RoomType = DEFAULT_ROOM_TYPE): string {
    return `${ROOM_TYPE_PREFIX[type]}/${preview ? "preview/" : ""}${slug}`;
}

export function roomTypeLabel(type: RoomType | undefined): string {
    return ROOM_TYPE_LABEL[type ?? DEFAULT_ROOM_TYPE];
}

export function isRoomType(value: unknown): value is RoomType {
    return typeof value === "string" && (ROOM_TYPES as readonly string[]).includes(value);
}

// The room type whose URL segment is `segment` ("deal-room" -> deal_room).
// The legacy "rooms" segment reads as a deal room.
export function roomTypeFromSegment(segment: string): RoomType | null {
    const prefix = `/${segment}`;
    if (prefix === LEGACY_ROOM_PREFIX) return DEFAULT_ROOM_TYPE;
    const hit = (Object.keys(ROOM_TYPE_PREFIX) as RoomType[]).find((t) => ROOM_TYPE_PREFIX[t] === prefix);
    return hit ?? null;
}

export interface ParsedRoomPath {
    type: RoomType;
    preview: boolean;
    slug: string;
    page: string | undefined;
}

// Parse "/<type-segment>/[preview/]<slug>[/<page>]". Null for any other path
// (the root, /sign-in, an unknown first segment).
export function parseRoomPath(pathname: string): ParsedRoomPath | null {
    const parts = pathname.split("?")[0].split("#")[0].split("/").filter(Boolean);
    if (!parts.length) return null;
    const type = roomTypeFromSegment(parts[0]);
    if (!type) return null;
    const preview = parts[1] === "preview";
    const rest = preview ? parts.slice(2) : parts.slice(1);
    if (!rest[0]) return null;
    return { type, preview, slug: rest[0], page: rest[1] };
}
